/**
 * Wallet Service
 * Handles all wallet-related API calls and business logic
 * Integrates with the backend wallet endpoints
 */

import { getServerBaseUrl } from '@/utils/serverUrl'
import { getStoredSessionToken } from '../utils/sessionToken'
import { forbiddenText } from '../utils/rbacMessages'

export interface CompanyWallet {
  id: string
  company_id: string
  balance: number
  currency: string
  status: 'active' | 'suspended' | 'closed'
  minimum_balance: number
  auto_top_up_enabled: boolean
  auto_top_up_threshold: number
  auto_top_up_amount: number
  last_updated: Date
  created_by: string
  created_at: Date
  updated_at: Date
}

export interface WalletTransaction {
  id: string
  company_id: string
  wallet_id: string
  transaction_type: 'top_up' | 'deduction' | 'refund' | 'adjustment' | 'auto_top_up'
  amount: number
  currency: string
  balance_before: number
  balance_after: number
  status: 'pending' | 'completed' | 'failed' | 'cancelled'
  description?: string
  reference_id?: string
  stripe_payment_intent_id?: string
  stripe_charge_id?: string
  metadata?: Record<string, any>
  processed_at?: Date
  created_by: string
  created_at: Date
  updated_at: Date
}

export interface TopUpRequest {
  amount: number
  currency?: string
  payment_method_id?: string
  description?: string
}

export interface AutoTopUpConfig {
  enabled: boolean
  threshold: number
  amount: number
}

export interface WalletStats {
  total_balance: number
  total_transactions: number
  total_top_ups: number
  total_deductions: number
  last_transaction_date?: Date
  auto_top_up_enabled: boolean
}

export interface PaymentIntent {
  id: string
  client_secret: string
  amount: number
  currency: string
  status: string
  metadata?: Record<string, string>
}

class WalletService {
  private serverBaseUrl: string

  constructor() {
    this.serverBaseUrl = getServerBaseUrl()
  }

  private getAuthHeaders(includeContentType: boolean = true): Record<string, string> {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${getStoredSessionToken()}`
    }

    if (includeContentType) {
      headers['Content-Type'] = 'application/json'
    }

    return headers
  }

  /**
   * Get wallet details for a company - automatically initializes wallet if not found
   */
  async getWallet(companyId: string): Promise<{ success: boolean; wallet?: CompanyWallet; error?: string; initializing?: boolean }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/wallet/${companyId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      })

      const data = await response.json()

      if (!response.ok) {
        return { success: false, error: forbiddenText(response.status, data) ?? (data.error || 'Failed to get wallet') }
      }

      return { success: true, wallet: data.wallet }

    } catch (error: any) {
      console.error('Error getting wallet:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Initiate wallet top-up with invoice generation
   */
  async initiateTopUpWithInvoice(
    companyId: string, 
    request: TopUpRequest
  ): Promise<{ success: boolean; invoice?: any; transaction?: WalletTransaction; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/wallet/${companyId}/top-up-invoice`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(request)
      })

      const data = await response.json()

      if (!response.ok) {
        return { success: false, error: forbiddenText(response.status, data) ?? (data.error || 'Failed to initiate top-up invoice') }
      }

      return { 
        success: true, 
        invoice: data.invoice,
        transaction: data.transaction
      }

    } catch (error: any) {
      console.error('Error initiating top-up invoice:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Initiate wallet top-up (legacy method)
   */
  async initiateTopUp(
    companyId: string, 
    request: TopUpRequest
  ): Promise<{ success: boolean; payment_intent?: PaymentIntent; transaction?: WalletTransaction; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/wallet/${companyId}/top-up`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(request)
      })

      const data = await response.json()

      if (!response.ok) {
        return { success: false, error: forbiddenText(response.status, data) ?? (data.error || 'Failed to initiate top-up') }
      }

      return { 
        success: true, 
        payment_intent: data.payment_intent,
        transaction: data.transaction
      }

    } catch (error: any) {
      console.error('Error initiating top-up:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Confirm payment completion
   */
  async confirmPayment(
    companyId: string, 
    paymentIntentId: string
  ): Promise<{ success: boolean; transaction?: WalletTransaction; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/wallet/${companyId}/confirm-payment`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ payment_intent_id: paymentIntentId })
      })

      const data = await response.json()

      if (!response.ok) {
        return { success: false, error: forbiddenText(response.status, data) ?? (data.error || 'Failed to confirm payment') }
      }

      return { success: true, transaction: data.transaction }

    } catch (error: any) {
      console.error('Error confirming payment:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Get wallet transaction history
   */
  async getTransactions(
    companyId: string, 
    limit: number = 50, 
    offset: number = 0
  ): Promise<{ success: boolean; transactions?: WalletTransaction[]; total?: number; error?: string }> {
    try {
      const response = await fetch(
        `${this.serverBaseUrl}/api/wallet/${companyId}/transactions?limit=${limit}&offset=${offset}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders()
        }
      )

      const data = await response.json()

      if (!response.ok) {
        return { success: false, error: forbiddenText(response.status, data) ?? (data.error || 'Failed to get transactions') }
      }

      return { 
        success: true, 
        transactions: data.transactions,
        total: data.total
      }

    } catch (error: any) {
      console.error('Error getting transactions:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Refresh a wallet transaction's status by ID
   */
  async refreshTransaction(
    companyId: string,
    transactionId: string
  ): Promise<{ success: boolean; transaction?: WalletTransaction; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/wallet/${companyId}/transactions/${transactionId}/refresh`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      })

      const data = await response.json()

      if (!response.ok) {
        return { success: false, error: forbiddenText(response.status, data) ?? (data.error || 'Failed to refresh transaction') }
      }

      return { success: true, transaction: data.transaction }

    } catch (error: any) {
      console.error('Error refreshing transaction:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Configure auto top-up
   */
  async configureAutoTopUp(
    companyId: string, 
    config: AutoTopUpConfig
  ): Promise<{ success: boolean; wallet?: CompanyWallet; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/wallet/${companyId}/auto-top-up`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(config)
      })

      const data = await response.json()

      if (!response.ok) {
        return { success: false, error: forbiddenText(response.status, data) ?? (data.error || 'Failed to configure auto top-up') }
      }

      return { success: true, wallet: data.wallet }

    } catch (error: any) {
      console.error('Error configuring auto top-up:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Get wallet statistics - automatically initializes wallet if not found
   */
  async getWalletStats(companyId: string): Promise<{ success: boolean; stats?: WalletStats; error?: string; initializing?: boolean }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/wallet/${companyId}/stats`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      })

      const data = await response.json()

      if (!response.ok) {
        return { success: false, error: forbiddenText(response.status, data) ?? (data.error || 'Failed to get wallet stats') }
      }

      return { success: true, stats: data.stats }

    } catch (error: any) {
      console.error('Error getting wallet stats:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Download wallet invoice
   */
  async downloadInvoice(invoiceId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.serverBaseUrl}/api/stripe/download-invoice/${invoiceId}`, {
        method: 'GET',
        headers: this.getAuthHeaders(false)
      })

      if (!response.ok) {
        const errorData = await response.json()
        return { success: false, error: forbiddenText(response.status, errorData) ?? (errorData.error || 'Failed to download invoice') }
      }

      // Get the filename from the Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition')
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') 
        : `invoice-${invoiceId}.pdf`

      // Create blob and download
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      return { success: true }

    } catch (error: any) {
      console.error('Error downloading invoice:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Format currency amount for display
   */
  formatCurrency(amount: number, currency: string = 'usd'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount)
  }

  /**
   * Format transaction type for display
   */
  formatTransactionType(type: string): string {
    const typeMap: Record<string, string> = {
      'top_up': 'Top Up',
      'deduction': 'Deduction',
      'refund': 'Refund',
      'adjustment': 'Adjustment',
      'auto_top_up': 'Auto Top Up'
    }
    return typeMap[type] || type
  }

  /**
   * Get transaction status color
   */
  getTransactionStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      'pending': '#ff9800',
      'completed': '#4caf50',
      'failed': '#f44336',
      'cancelled': '#9e9e9e'
    }
    return colorMap[status] || '#9e9e9e'
  }

  /**
   * Validate top-up amount
   */
  validateTopUpAmount(amount: number): { valid: boolean; error?: string } {
    if (amount < 5) {
      return { valid: false, error: 'Minimum top-up amount is $5.00' }
    }
    if (amount > 10000) {
      return { valid: false, error: 'Maximum top-up amount is $10,000.00' }
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      return { valid: false, error: 'Please enter a valid amount' }
    }
    if (!Number.isInteger(amount)) {
      return { valid: false, error: 'Top-up amount must be a whole number (no decimals)' }
    }
    return { valid: true }
  }
}

export const walletService = new WalletService()
